// SPDX-License-Identifier: GPL-2.0-or-later
/**
 * The shared look of <einvoice-field-browser> and <einvoice-rule-browser>: the design kit itself, plus the few
 * rules the kit has no block for.
 *
 * ⚠️ **THE KIT IS LOADED, NOT COPIED** ( since 19.10.2026, finding B193 ). Until then this file carried a copy of the
 * kit's blocks ( `.inp`, `.sel`, `.btn`, `.panel`, `.panel-head`, `.badge` … ) with Kit H's values as fallbacks, and
 * the Technical Details carried a second copy in svtd.css. A copy does not follow the kit switch: on a site that chose
 * Kit A or an uploaded kit the browsers stayed in Kit H. Now the host names the kit's token file and building blocks in
 * `data-css`, the browser links them into its shadow root through kitStyleLinks(), and the markup uses the kit's own
 * classes: `.filterbar` for the toolbar, `.field` with `label`, `.panel`, `.panel-head`, `.icon-btn`, `.badge`,
 * `.note.warn`, `.empty`, `.mono`, `.btn`.
 *
 * ⚠️ **WHAT STAYS HERE HAS NO BLOCK IN THE KIT**, and every value in it is a token: the grid of the virtual list
 * ( `.br-cols`, `.br-row`, `.br-list` ), the inside of the detail panel ( `.br-detail` ), the colour of a code. The own
 * classes carry the prefix `br-`, so that no rule here lands on a class of the kit; the kit gate
 * ( `_claude-tools/check-kit-usage.mjs` ) stops a build that styles a kit block.
 *
 * ⚠️ **THE STYLESHEETS COME BEFORE THIS TEXT.** kitStyleLinks() appends the `<link>`s first and the `<style>` after
 * them, so the rules here may build on the kit but never have to fight it.
 *
 * ⚠️ **THE FONTS ARE NOT LOADED HERE.** `@font-face` inside a shadow root is ignored by browsers; the host page has to
 * declare Inter, Space Grotesk and JetBrains Mono ( the plugin's svtd.css does ).
 */

import { kitStylesheets } from '../kit/kit-tokens.mjs';

export const BROWSER_STYLE = `
:host { display: block; }
/* ⚠️ THE FONT ON THE INNER WRAPPER, NOT ONLY ON :host. A theme rule that selects the host element from outside
   ( "body.open_sans *" ) beats :host, and the shadow tree would inherit the theme's font. No outer rule reaches .wrap. */
.wrap { font-family: var( --font ); font-size: var( --fs-sm ); line-height: var( --lh ); color: var( --text ); text-align: left; }
[hidden] { display: none !important; }

/* The search field grows, with a floor: the kit's .grow ( flex: 1 ) let it shrink to a stub among the selects. */
.br-grow { flex: 1 1 220px; }

/* ⚠️ ONE GRID FOR HEAD AND ROWS ( --cols, set per browser ), so the column headings stand exactly above their cells
   although the rows are absolutely positioned by VirtualList. The head takes the look of the kit's ".tbl thead th"
   ( muted text, a 1px line ), so it matches the reference browser's table, which is a real "table.tbl". */
.br-cols, .br-row { display: grid; grid-template-columns: var( --cols ); column-gap: 12px; align-items: center; padding: 0 14px; }
.br-cols { padding-top: 8px; padding-bottom: 8px; font-family: var( --ph-ff, var( --mono ) ); font-size: var( --fs-xs ); text-transform: uppercase; letter-spacing: .05em; color: var( --muted ); background: var( --surface-2 ); border-bottom: 1px solid var( --border ); font-weight: 700; white-space: nowrap; }
.br-cols > span, .br-row > span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.br-list { position: relative; overflow-y: auto; background: var( --surface ); }
.br-row { border-bottom: 1px solid var( --border ); cursor: pointer; }
.br-row.br-alt { background: var( --zebra ); }
.br-row:hover { background: var( --surface-2 ); }
.br-code { color: var( --accent ); }
.br-loading { padding: 12px 14px; color: var( --muted ); }

.br-detail-panel { margin-top: 12px; }
.br-detail p { margin: 0 0 8px; }
.br-detail h4 { font-family: var( --mono ); font-size: var( --fs-xs ); font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: var( --muted ); margin: 14px 0 6px; padding-top: 7px; border-top: 1px solid var( --border ); }
.br-detail dl { display: grid; grid-template-columns: max-content 1fr; gap: 4px 16px; margin: 8px 0; }
.br-detail dt { font-size: var( --fs-xs ); color: var( --muted ); font-weight: 650; padding-top: 2px; }
.br-detail dd { margin: 0; min-width: 0; overflow-wrap: anywhere; }
.br-detail pre { white-space: pre-wrap; word-break: break-word; font-family: var( --mono ); font-size: var( --fs-xs ); background: var( --code-bg, var( --surface-2 ) ); border: 1px solid var( --border ); border-radius: var( --radius ); padding: 8px 10px; margin: 6px 0 0; }
.br-detail details { margin: 8px 0; }
.br-detail summary { cursor: pointer; font-size: var( --fs-xs ); color: var( --muted ); font-weight: 650; }
.br-detail ul { margin: 0; padding-left: 18px; }
`;


/**
 * Links the kit into a shadow root: the addresses from `data-css`, in their order, then the own rules.
 *
 * ⚠️ **WITHOUT `data-css` THE BROWSER HAS NO KIT AND SAYS SO IN THE CONSOLE.** A host that forgets the attribute would
 * get plain form controls; that should be found by the one who builds the host, not by a visitor.
 *
 * @param {ShadowRoot} shade
 * @param {HTMLElement} host   the element, for its `data-css`
 * @param {string} style       the own rules of the browser
 * @return {void}
 */
export function kitStyleLinks( shade, host, style ) {

	const urls = kitStylesheets( String( host.dataset.css || '' ).split( /[\s,]+/ ).filter( Boolean ) );

	if ( ! urls.length )
		console.warn( host.localName + ': no data-css, the design kit is missing' );

	for ( const url of urls ) {
		const link = document.createElement( 'link' );
		link.rel = 'stylesheet';
		link.href = url;
		shade.appendChild( link );
	}

	const el = document.createElement( 'style' );
	el.textContent = style;
	shade.appendChild( el );

}


/**
 * Escapes a text for HTML. One copy for both browsers instead of one in each.
 *
 * @param {*} s
 * @return {string}
 */

export function escapeHtml( s ) {
	return String( s ?? '' ).replace( /[&<>"']/g, ( c ) => ( { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ c ] ) );
}
